import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../components/ui/sheet';
import { toast } from '../hooks/use-toast';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import {
  ShieldCheck, AlertTriangle, TrendingDown, TrendingUp,
  RefreshCw, Camera, FileWarning, Code2, ServerCog,
  Copy, Wrench, Loader2,
} from 'lucide-react';

const fmtPct = (delta, base) => {
  if (!base) return '0%';
  const pct = (delta / base) * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
};

const StatCard = ({ icon: Icon, label, current, baseline, delta, color = 'blue' }) => {
  const COLOR_MAP = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  };
  const trend = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
  const trendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : null;
  const trendColor = trend === 'up' ? 'text-red-600' : trend === 'down' ? 'text-emerald-600' : 'text-slate-400';
  return (
    <Card className={`border-2 ${COLOR_MAP[color]}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <Icon className="w-4 h-4" />
          <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
        </div>
        <div className="text-3xl font-bold">{current}</div>
        <div className="flex items-center justify-between mt-2 text-xs">
          <span className="opacity-70">baseline: {baseline}</span>
          {trendIcon && (
            <span className={`flex items-center gap-1 font-semibold ${trendColor}`}>
              {(() => {
                const TrendIcon = trendIcon;
                return <TrendIcon className="w-3 h-3" />;
              })()}
              {delta > 0 ? '+' : ''}{delta} ({fmtPct(delta, baseline)})
            </span>
          )}
          {!trendIcon && <span className="text-emerald-600 font-semibold">sin cambios</span>}
        </div>
      </CardContent>
    </Card>
  );
};

export default function CalidadDashboard() {
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [snapping, setSnapping] = useState(false);
  // Iter 215: panel lateral con issues por archivo
  const [selectedFile, setSelectedFile] = useState(null); // {path, side}
  const [fileIssues, setFileIssues] = useState(null);
  const [loadingIssues, setLoadingIssues] = useState(false);
  const [filterSev, setFilterSev] = useState('all'); // all | error | warning

  const openFileIssues = useCallback(async (path, side) => {
    setSelectedFile({ path, side });
    setFileIssues(null);
    setLoadingIssues(true);
    setFilterSev('all');
    try {
      const res = await api.get('/quality/file-issues', {
        params: { path, side },
        timeout: 60000,
      });
      setFileIssues(res.data);
    } catch (err) {
      toast({
        title: 'Error al cargar issues',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
      setSelectedFile(null);
    } finally {
      setLoadingIssues(false);
    }
  }, []);

  const copyFilePath = useCallback((path, side) => {
    const full = side === 'frontend' ? `frontend/${path}` : `backend/${path}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(full);
      toast({ title: 'Ruta copiada', description: full });
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get('/quality/history?limit=30');
      setHistory(res.data.items || []);
    } catch (err) {
      console.error('[CalidadDashboard] history error:', err);
    }
  }, []);

  const loadCurrent = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const res = await api.get('/quality/current', { timeout: 120000 });
      setCurrent(res.data);
    } catch (err) {
      toast({
        title: 'Error al ejecutar linters',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await loadHistory();
      await loadCurrent(true);
    })();
  }, [loadCurrent, loadHistory]);

  const takeSnapshot = async () => {
    setSnapping(true);
    try {
      await api.post('/quality/snapshot', {}, { timeout: 120000 });
      toast({ title: 'Snapshot guardado' });
      await loadHistory();
    } catch (err) {
      toast({ title: 'Error al snapshot', description: err?.response?.data?.detail, variant: 'destructive' });
    } finally {
      setSnapping(false);
    }
  };

  const chartData = history.map((h) => ({
    fecha: h.fecha_iso?.split('T')[0],
    'FE Errors': h.frontend_errors,
    'FE Warnings': h.frontend_warnings,
    'BE Errors': h.backend_errors,
  }));

  if (loading) {
    return (
      <div className="p-4 space-y-3">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-32" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!current) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <p>No se pudo cargar la información de calidad.</p>
        <Button onClick={() => loadCurrent()} className="mt-3">Reintentar</Button>
      </div>
    );
  }

  const { baseline, current: cur, delta, regression } = current;

  return (
    <div className="p-4 space-y-4" data-testid="calidad-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="w-6 h-6" />
            Dashboard de Calidad de Código
          </h1>
          <p className="text-sm text-muted-foreground">
            Estado actual vs baseline · {history.length} snapshots históricos
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm"
            onClick={() => loadCurrent()} disabled={refreshing}
            data-testid="btn-refresh-quality"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Ejecutando…' : 'Re-ejecutar'}
          </Button>
          <Button
            size="sm" onClick={takeSnapshot} disabled={snapping}
            data-testid="btn-snapshot"
          >
            <Camera className="w-4 h-4 mr-1" />
            {snapping ? 'Guardando…' : 'Snapshot'}
          </Button>
        </div>
      </div>

      {/* Banner regresión / fallback */}
      {current.fallback && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-3 flex items-center gap-2 text-amber-800">
            <AlertTriangle className="w-5 h-5" />
            <div className="text-sm">
              <strong>Modo histórico:</strong> {current.fallback_reason}
            </div>
          </CardContent>
        </Card>
      )}
      {!current.fallback && regression && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="p-3 flex items-center gap-2 text-red-800">
            <AlertTriangle className="w-5 h-5" />
            <div className="text-sm">
              <strong>Regresión detectada</strong> sobre el baseline. Considerá revisar los cambios recientes.
            </div>
          </CardContent>
        </Card>
      )}
      {!current.fallback && !regression && (
        <Card className="border-emerald-300 bg-emerald-50">
          <CardContent className="p-3 flex items-center gap-2 text-emerald-800">
            <ShieldCheck className="w-5 h-5" />
            <div className="text-sm">
              <strong>Calidad OK</strong> · errors no han subido sobre el baseline. Última verificación: {new Date(current.checked_at).toLocaleString('es-CL')}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cards de estado */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCard
          icon={Code2}
          label="Frontend Errors"
          current={cur.frontend_errors}
          baseline={baseline.frontend_errors}
          delta={delta.frontend_errors}
          color="red"
        />
        <StatCard
          icon={FileWarning}
          label="Frontend Warnings"
          current={cur.frontend_warnings}
          baseline={baseline.frontend_warnings}
          delta={delta.frontend_warnings}
          color="amber"
        />
        <StatCard
          icon={ServerCog}
          label="Backend Errors"
          current={cur.backend_errors}
          baseline={baseline.backend_errors}
          delta={delta.backend_errors}
          color="blue"
        />
      </div>

      {/* Línea de tiempo */}
      {chartData.length > 1 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-sm mb-3">Evolución temporal</h3>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line type="monotone" dataKey="FE Errors" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="FE Warnings" stroke="#d97706" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="BE Errors" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {chartData.length <= 1 && (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            Tomá al menos 2 snapshots para ver la evolución temporal. Click en <strong>Snapshot</strong> para guardar el estado actual.
          </CardContent>
        </Card>
      )}

      {/* Top archivos */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-1">
              <Code2 className="w-4 h-4" /> Top archivos Frontend
            </h3>
            {current.frontend.top_files.length === 0 ? (
              <p className="text-xs text-muted-foreground">Sin issues 🎉</p>
            ) : (
              <div className="space-y-1">
                {current.frontend.top_files.map((f) => (
                  <button
                    key={f.path}
                    type="button"
                    onClick={() => openFileIssues(f.path, 'frontend')}
                    data-testid={`fe-file-row-${f.path}`}
                    className="w-full flex items-center gap-2 text-xs px-2 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-left"
                    title={`Ver ${f.errors + f.warnings} issue(s) en ${f.path}`}
                  >
                    <code className="font-mono text-[11px] flex-1 truncate">{f.path}</code>
                    {f.errors > 0 && (
                      <Badge variant="outline" className="text-[10px] h-5 bg-red-50 text-red-700 border-red-200">
                        {f.errors}E
                      </Badge>
                    )}
                    {f.warnings > 0 && (
                      <Badge variant="outline" className="text-[10px] h-5 bg-amber-50 text-amber-700 border-amber-200">
                        {f.warnings}W
                      </Badge>
                    )}
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-1">
              <ServerCog className="w-4 h-4" /> Top archivos Backend
            </h3>
            {current.backend.top_files.length === 0 ? (
              <p className="text-xs text-muted-foreground">Sin issues 🎉</p>
            ) : (
              <div className="space-y-1">
                {current.backend.top_files.map((f) => (
                  <button
                    key={f.path}
                    type="button"
                    onClick={() => openFileIssues(f.path, 'backend')}
                    data-testid={`be-file-row-${f.path}`}
                    className="w-full flex items-center gap-2 text-xs px-2 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-left"
                    title={`Ver ${f.count} issue(s) en ${f.path}`}
                  >
                    <code className="font-mono text-[11px] flex-1 truncate">{f.path}</code>
                    <Badge variant="outline" className="text-[10px] h-5 bg-blue-50 text-blue-700 border-blue-200">
                      {f.count}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
            {current.backend.top_rules?.length > 0 && (
              <div className="mt-3 pt-3 border-t">
                <p className="text-[11px] font-semibold text-muted-foreground mb-1">Reglas más violadas:</p>
                <div className="flex flex-wrap gap-1">
                  {current.backend.top_rules.map((r) => (
                    <Badge key={r.rule} variant="outline" className="text-[10px]">
                      {r.rule} <span className="ml-1 opacity-70">×{r.count}</span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="text-[11px] text-muted-foreground border-t pt-3">
        💡 Tip: corré <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">bash scripts/check-quality.sh --fix</code> para auto-fix lo que sea seguro,
        y luego <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">--update-baseline</code> cuando bajes intencionalmente el conteo.
      </div>

      {/* Iter 215: panel lateral con issues del archivo seleccionado */}
      <Sheet
        open={!!selectedFile}
        onOpenChange={(open) => { if (!open) setSelectedFile(null); }}
      >
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="sheet-file-issues">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-base">
              {selectedFile?.side === 'frontend' ? <Code2 className="w-4 h-4" /> : <ServerCog className="w-4 h-4" />}
              <span className="truncate font-mono text-xs">{selectedFile?.path}</span>
            </SheetTitle>
            <SheetDescription className="text-xs">
              {loadingIssues
                ? 'Ejecutando linter sobre el archivo…'
                : fileIssues
                  ? `${fileIssues.total} issue(s): ${fileIssues.errors} error(es), ${fileIssues.warnings} warning(s)`
                  : ''}
            </SheetDescription>
          </SheetHeader>

          {loadingIssues && (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span className="text-sm">Cargando issues…</span>
            </div>
          )}

          {!loadingIssues && fileIssues && (
            <div className="mt-4 space-y-3">
              {/* Acciones rápidas */}
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  size="sm" variant="outline"
                  onClick={() => copyFilePath(selectedFile.path, selectedFile.side)}
                  data-testid="btn-copy-path"
                >
                  <Copy className="w-3 h-3 mr-1" /> Copiar ruta
                </Button>
                {/* Filtros por severidad */}
                <div className="flex items-center gap-1 ml-auto">
                  {['all', 'error', 'warning'].map((s) => {
                    const cnt = s === 'all'
                      ? fileIssues.total
                      : s === 'error' ? fileIssues.errors : fileIssues.warnings;
                    return (
                      <Button
                        key={s}
                        size="sm"
                        variant={filterSev === s ? 'default' : 'ghost'}
                        onClick={() => setFilterSev(s)}
                        className="h-7 text-[11px] px-2"
                        data-testid={`btn-filter-${s}`}
                      >
                        {s === 'all' ? 'Todos' : s === 'error' ? 'Errors' : 'Warnings'}
                        <span className="ml-1 opacity-70">({cnt})</span>
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Top reglas del archivo */}
              {fileIssues.top_rules?.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
                  <span className="text-muted-foreground font-semibold">Reglas:</span>
                  {fileIssues.top_rules.map((r) => (
                    <Badge key={r.rule} variant="outline" className="text-[10px] h-5 font-mono">
                      {r.rule}<span className="ml-1 opacity-70">×{r.count}</span>
                    </Badge>
                  ))}
                </div>
              )}

              {/* Lista de issues */}
              <div className="space-y-1.5 max-h-[calc(100vh-280px)] overflow-y-auto pr-1">
                {fileIssues.issues.length === 0 && (
                  <p className="text-center text-xs text-muted-foreground py-6">Sin issues 🎉</p>
                )}
                {fileIssues.issues
                  .filter((i) => filterSev === 'all' || i.severity === filterSev)
                  .map((i, idx) => (
                    <div
                      key={`${i.line}-${i.column}-${idx}`}
                      data-testid={`issue-row-${idx}`}
                      className={`rounded border p-2.5 text-xs ${
                        i.severity === 'error'
                          ? 'border-red-200 bg-red-50/50 dark:bg-red-950/20 dark:border-red-900'
                          : 'border-amber-200 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-900'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Badge
                          variant="outline"
                          className={`text-[9px] h-4 ${
                            i.severity === 'error'
                              ? 'bg-red-100 text-red-700 border-red-300'
                              : 'bg-amber-100 text-amber-700 border-amber-300'
                          }`}
                        >
                          {i.severity === 'error' ? 'ERROR' : 'WARN'}
                        </Badge>
                        <code className="font-mono text-[10px] opacity-70">
                          línea {i.line}{i.column ? `:${i.column}` : ''}
                        </code>
                        {i.rule && (
                          <Badge variant="outline" className="text-[10px] h-4 font-mono ml-auto">
                            {i.rule}
                          </Badge>
                        )}
                        {i.fixable && (
                          <Badge
                            variant="outline"
                            className="text-[9px] h-4 bg-emerald-50 text-emerald-700 border-emerald-200"
                            title="Auto-fixable con linter --fix"
                          >
                            <Wrench className="w-2.5 h-2.5 mr-0.5" />
                            fix
                          </Badge>
                        )}
                      </div>
                      <p className="text-[11px] leading-snug">{i.message}</p>
                    </div>
                  ))}
              </div>

              {/* Hint de auto-fix global */}
              {fileIssues.issues.some((i) => i.fixable) && (
                <div className="text-[10px] text-muted-foreground border-t pt-2">
                  💡 Issues marcados <Wrench className="inline w-2.5 h-2.5" /> <strong>fix</strong> se auto-corrigen con{' '}
                  <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">bash scripts/check-quality.sh --fix</code>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
